import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { TransactionType, TransactionStatus } from '../../types/drawdown';
import { Search } from 'lucide-react';

export interface TransactionFilters {
  transaction_type?: TransactionType;
  status?: TransactionStatus;
  date_from?: string;
  date_to?: string;
  reference?: string;
}

const TRANSACTION_TYPE_OPTIONS = [
  { value: TransactionType.DRAWDOWN, label: 'Drawdown' },
  { value: TransactionType.INVESTMENT_PURCHASE, label: 'Investment' },
  { value: TransactionType.FUND_FEE_EXPENSE, label: 'Fee/Expense' },
  { value: TransactionType.DISTRIBUTION, label: 'Distribution' },
  { value: TransactionType.LP_TRANSFER, label: 'LP Transfer' },
  { value: TransactionType.MANUAL_ADJUSTMENT, label: 'Manual' },
  { value: TransactionType.VALUATION_CHANGE, label: 'Valuation' },
  { value: TransactionType.ASSET_SALE, label: 'Asset Sale' },
  {
    value: TransactionType.MANAGER_CURRENT_ACCOUNT,
    label: 'Manager current account',
  },
];

const STATUS_OPTIONS = [
  { value: TransactionStatus.APPROVED, label: 'Approved' },
  { value: TransactionStatus.POSTED, label: 'Posted' },
  { value: TransactionStatus.REVERSED, label: 'Reversed' },
];

interface TransactionFilterBarProps {
  filters: TransactionFilters;
  onFiltersChange: (filters: TransactionFilters) => void;
  onClearFilters: () => void;
}

export function TransactionFilterBar({
  filters,
  onFiltersChange,
  onClearFilters,
}: TransactionFilterBarProps) {
  const hasActiveFilters =
    filters.transaction_type ||
    filters.status ||
    filters.date_from ||
    filters.date_to ||
    filters.reference;

  return (
    <div className="flex items-center justify-start gap-2">
      <Select
        value={filters.transaction_type ?? '_all'}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            transaction_type:
              value === '_all' ? undefined : (value as TransactionType),
          })
        }
      >
        <SelectTrigger className="w-36 h-8">
          <SelectValue placeholder="All types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Types</SelectItem>
          {TRANSACTION_TYPE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.status ?? '_all'}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            status: value === '_all' ? undefined : (value as TransactionStatus),
          })
        }
      >
        <SelectTrigger className="w-32 h-8">
          <SelectValue placeholder="All statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Statuses</SelectItem>
          {STATUS_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        type="date"
        placeholder="From"
        value={filters.date_from || ''}
        onChange={(e) =>
          onFiltersChange({
            ...filters,
            date_from: e.target.value || undefined,
          })
        }
        className="w-36 h-8"
      />

      <Input
        type="date"
        placeholder="To"
        value={filters.date_to || ''}
        onChange={(e) =>
          onFiltersChange({ ...filters, date_to: e.target.value || undefined })
        }
        className="w-36 h-8"
      />

      <div className="relative">
        <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
        <Input
          value={filters.reference ?? ''}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              reference: e.target.value || undefined,
            })
          }
          placeholder="Search reference..."
          className="pl-8 w-[180px] h-8"
          selectAllOnFocus={false}
        />
      </div>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="h-8 px-2 text-xs"
        >
          Clear
        </Button>
      )}
    </div>
  );
}
